import { ReactNode } from 'react'

import Link from '@/atoms/Link/Link'
import FooterRights from '@/cmp/molecules/Footer/FooterRights'
import { ReactComponent as PepperLogo } from 'public/assets/icons/PepperLogoTrimmed.svg'

interface FooterSectionProps {
  title: string
  links: FooterLink[]
  children?: ReactNode
}

function Footer(): JSX.Element {
  return (
    <footer>
      <section className="container grid gap-x-8 sm:grid-cols-2 lg:grid-cols-4 | px-5 pt-12 pb-6 sm:px-10 lg:px-6 mx-auto">
        <div className="sm:col-span-2 | mb-10 lg:mb-0 lg:pr-24">
          <Link
            className="inline-block focus:outline-none"
            href="/"
            title="Homepage"
          >
            <PepperLogo className="h-14 w-auto" />
          </Link>
          <p className="text-blue-web-90 text-base leading-3 | max-w-md mt-8">
            {`Pepper is the organic growth engine that gets you found in search and cited in AI answers — and you don't have to run it yourself. Our platform finds what's holding you back across SEO, GEO, and content, our AI agents do the work at scale, and our growth team drives it all to outcomes. One platform, one team, trusted by 250+ enterprises.`}
          </p>
        </div>

        <div>
          <FooterSection title="Product" links={productLinks} />
          <FooterSection title="Solutions" links={solutionsLinks} />
        </div>

        <div>
          <FooterSection title="Resources" links={resourcesLinks} />
          <FooterSection title="About us" links={aboutLinks}>
            <a
              className="text-xs font-medium | rounded-sm | px-2 py-0.5 | bg-blue-40 text-white"
              href="https://creators.peppercontent.io"
              target="_blank"
              rel="noreferrer"
            >
              Join as a Creator&nbsp;
              <span role="img" aria-label="creators">
                ✍️
              </span>
            </a>
          </FooterSection>
        </div>
      </section>

      <hr className="container  |  border-blue-20  |  px-5 lg:mx-auto" />

      <FooterRights />
    </footer>
  )
}

function FooterSection({
  title,
  links,
  children
}: FooterSectionProps): JSX.Element {
  return (
    <div className="mb-6 lg:mb-9">
      <h3 className="text-yellow-70 text-small font-medium uppercase tracking-widest  | mb-5">
        {title}
      </h3>
      <ul className="space-y-2.5">
        {links.map(item => (
          <li key={item.slug}>
            <Link
              className="text-blue-web-90 text-sm | inline-block focus:outline-none focus:text-blue-50 hover:text-blue-50"
              href={item.slug}
            >
              {item.page_title}
            </Link>
          </li>
        ))}
        {children && <li className="pt-1">{children}</li>}
      </ul>
    </div>
  )
}

const productLinks = [
  { slug: '/platform/', page_title: 'Platform' },
  { slug: 'https://platform.pepper.inc/help', page_title: 'Documentation' }
]

const solutionsLinks = [
  { slug: '/solutions/seo/', page_title: 'SEO+GEO' },
  { slug: '/solutions/content/', page_title: 'Content' },
  { slug: '/video-services/', page_title: 'Creative' }
]

const resourcesLinks = [
  { slug: 'https://www.pepper.inc/blog/', page_title: 'GEO Hub' },
  { slug: '/case-study/', page_title: 'Customers' }
]

const aboutLinks = [
  { slug: '/about-us/', page_title: 'Culture' },
  { slug: '/careers/', page_title: 'Career' }
]

export default Footer
